/**
 * What the customer is buying, before it becomes an order.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ----------------------------------
 * It does not decide what anything costs. The totals here are for the person
 * at the counter to read out; the server recalculates every one of them from
 * its own prices and tax rules when the order is placed.
 *
 * That is not duplication to be tidied away later — it is the point. A till
 * that could set its own prices is a till that can be argued with, and a
 * price list cached on a tablet since Tuesday would quietly undercharge all
 * week. The screen shows an estimate; the receipt shows the truth. They agree
 * because both use the same rounding, from @foodrun/shared.
 *
 * WHY LINES ARE NOT KEYED ON THE PRODUCT
 * --------------------------------------
 * Two Zinger burgers, one with extra cheese, are two different things to the
 * kitchen and two different prices. Keying on the product alone would merge
 * them into a quantity of two and lose the cheese — silently, and only
 * noticed when the wrong food reaches the table.
 */
import { create } from 'zustand';
import { round2, sumMoney } from '@foodrun/shared';

/**
 * A stable identity for a line: the product, its variant, and its extras.
 *
 * Modifier ids are sorted so that choosing cheese-then-bacon and
 * bacon-then-cheese produce the same line rather than two identical-looking
 * ones the customer is charged for twice.
 */
function lineKey({ productId, variantId, modifiers = [], components = null }) {
  const extras = modifiers
    .map((m) => m.optionId)
    .sort()
    .join('+');

  // A deal's choices tell two otherwise-identical lines apart: two of the same
  // pizza is not the same line as one of each — down to the size each was
  // ordered in, so a Large and a Regular of the same pizza also stay apart.
  const chose = components
    ? components
        .map((c) => {
          const mods = (c.modifiers ?? [])
            .map((m) => m.optionId)
            .sort()
            .join('+');
          return `${c.productId}x${c.quantity}${mods ? `(${mods})` : ''}`;
        })
        .sort()
        .join(',')
    : '';

  return [productId, variantId ?? '', extras, chose].join('|');
}

/** What one line costs, before any order-level discount or tax. */
function lineTotal(line) {
  const extras = sumMoney(line.modifiers?.map((m) => m.priceAdjustment ?? 0) ?? []);
  return round2((line.unitPrice + extras) * line.quantity);
}

export const useCart = create((set, get) => ({
  lines: [],
  orderType: 'dine_in',
  deliveryZoneId: null,
  /**
   * A delivery fee the cashier set by hand, overriding the zone's or the flat
   * default. Null means "work it out as usual" — the common case.
   */
  deliveryFee: null,
  customerName: '',
  customerPhone: '',
  /**
   * Which known customer this order is for, when the cashier picked one.
   *
   * Only set by choosing from the suggestions. Typed details alone do not set
   * it — the branch matches those on the phone number itself, and a guess made
   * here would be a worse one than the branch's.
   */
  customerId: null,
  tableNumber: '',
  notes: '',
  /** The Foodpanda app's order number, keyed in on a Foodpanda order. */
  externalOrderId: '',

  /**
   * The order this cart is adding to, when a table wants more.
   *
   * Null for a normal new order. Set to { orderId, label } while adding another
   * round, so the send goes to that bill's items rather than creating a second
   * order on the same table — the floor plan showing one table twice is exactly
   * the bug this avoids.
   */
  addingTo: null,

  /**
   * Where a delivery is going, and where that is on a map.
   *
   * The address is what gets printed and what the rider reads. The point is
   * what stops them reading it — a pin opens their maps app on the door
   * rather than on a search for "Street 2".
   *
   * Both empty on a dine-in or takeaway, and left out of the order entirely
   * rather than sent as nulls.
   */
  deliveryAddress: '',
  deliveryLatitude: null,
  deliveryLongitude: null,

  /** Which table a dine-in order is sitting at. */
  tableId: null,

  /** An offer somebody has applied, by its code. */
  promotionCode: '',
  /** A discount typed in, within whatever limit this person may give. */
  discount: null,

  /**
   * Add a product, or add to the matching line if one is already there.
   *
   * @param {object} product     as returned by /api/menu/products
   * @param {object} [options]
   * @param {Array}  [options.modifiers]  [{ optionId, name, priceAdjustment }]
   */
  add(product, { modifiers = [], variantId, notes, components = null } = {}) {
    const key = lineKey({ productId: product.id, variantId, modifiers, components });

    set((state) => {
      const existing = state.lines.find((l) => l.key === key);

      if (existing) {
        return {
          lines: state.lines.map((l) => (l.key === key ? { ...l, quantity: l.quantity + 1 } : l)),
        };
      }

      return {
        lines: [
          ...state.lines,
          {
            key,
            productId: product.id,
            variantId,
            name: product.name,
            unitPrice: Number(product.price) || 0,
            quantity: 1,
            modifiers,
            notes,
            // The items chosen to fill a deal's slots, kept on the line so it
            // can be shown and sent with the order.
            components: components ?? null,
          },
        ],
      };
    });
  },

  /**
   * Change how many of a line.
   *
   * Dropping to zero removes it. A line showing "0" that still counts towards
   * the total is the kind of thing nobody notices until the drawer is short.
   */
  setQuantity(key, quantity) {
    const next = Math.floor(Number(quantity));

    if (!Number.isFinite(next) || next <= 0) {
      get().remove(key);
      return;
    }

    set((state) => ({
      lines: state.lines.map((l) => (l.key === key ? { ...l, quantity: next } : l)),
    }));
  },

  increment(key) {
    const line = get().lines.find((l) => l.key === key);
    if (line) get().setQuantity(key, line.quantity + 1);
  },

  decrement(key) {
    const line = get().lines.find((l) => l.key === key);
    if (line) get().setQuantity(key, line.quantity - 1);
  },

  remove(key) {
    set((state) => ({ lines: state.lines.filter((l) => l.key !== key) }));
  },

  /** Start again. Called after an order is placed, and by the Clear button. */
  clear() {
    set({
      lines: [],
      orderType: 'dine_in',
      deliveryZoneId: null,
      deliveryFee: null,
      customerName: '',
      customerPhone: '',
      customerId: null,
      tableNumber: '',
      notes: '',
      externalOrderId: '',
      // Forgotten with everything else. An offer left applied would come off
      // the next customer's bill too, and a table left set would seat the
      // next party at somebody else's table.
      tableId: null,
      promotionCode: '',
      discount: null,
      // And the address, for the same reason as the table but worse: a pin
      // left over sends the next customer's food to the last one's door.
      deliveryAddress: '',
      deliveryLatitude: null,
      deliveryLongitude: null,
      addingTo: null,
    });
  },

  /**
   * Begin adding another round to an existing bill.
   *
   * Starts from an empty cart so only the new dishes are sent — the bill's
   * own items are read back and re-priced on the server, not here.
   */
  startAdding({ orderId, orderNumber, label, orderType }) {
    get().clear();
    // The order type carries over only so the preview total taxes the new
    // items the way the bill will; the server re-prices the whole order.
    set({ addingTo: { orderId, orderNumber, label }, ...(orderType ? { orderType } : {}) });
  },

  /**
   * Load an existing unpaid order into the cart to edit the whole of it — every
   * line, its type, its table, who it is for. Editing it is then no different
   * from ringing up a new one; saving sends the whole thing back.
   *
   * `addingTo` doubles as the "this is an order being edited" flag, which the
   * panel already reads to show the banner and the single Save action.
   */
  loadOrder(data) {
    const label = data.tableNumber
      ? `Table ${data.tableNumber}`
      : `${
          { delivery: 'Delivery', foodpanda: 'Foodpanda', dine_in: 'Dine in' }[data.orderType] ??
          'Takeaway'
        } · ${data.orderNumber}`;

    const loaded = (data.items ?? []).map((it) => {
      const modifiers = (it.modifiers ?? []).map((m) => ({
        optionId: m.optionId,
        name: m.name,
        priceAdjustment: Number(m.priceAdjustment) || 0,
      }));
      // The stored unit price already includes the extras; the cart adds them
      // back on top, so hold the base here or the on-screen estimate would
      // count the extras twice. The server re-prices from its own catalogue
      // regardless, so this only affects what the cashier sees.
      const modSum = modifiers.reduce((s, m) => s + (m.priceAdjustment || 0), 0);
      return {
        key: lineKey({ productId: it.productId, variantId: it.variantId, modifiers }),
        productId: it.productId,
        variantId: it.variantId ?? undefined,
        name: it.name,
        unitPrice: round2((Number(it.unitPrice) || 0) - modSum),
        quantity: it.quantity,
        modifiers,
      };
    });

    /**
     * One line a dish, however many lines the order keeps it on.
     *
     * A waiter who orders a paratha and later adds two more leaves three lines
     * of one on the order — a round each. Loaded as three lines they shared a
     * key, and everything the cart does is by key: + on one raised all three,
     * so one more paratha became three more, and − at one removed the lot.
     *
     * The cart's own rule is one line for one thing — adding a dish already in
     * it raises the count rather than starting a second line — so an order
     * being edited is brought in the same way: "3 × paratha", which + and −
     * change by one. The server measures that total against all of the dish's
     * lines together, so nothing is lost by showing them as one.
     */
    const lines = [];
    for (const line of loaded) {
      const same = lines.find((l) => l.key === line.key);
      if (same) same.quantity += line.quantity;
      else lines.push(line);
    }

    set({
      lines,
      orderType: data.orderType,
      tableId: data.tableId ?? null,
      tableNumber: data.tableNumber ?? '',
      customerId: data.customerId ?? null,
      customerName: data.customerName ?? '',
      customerPhone: data.customerPhone ?? '',
      deliveryAddress: data.deliveryAddress ?? '',
      deliveryZoneId: data.deliveryZoneId ?? null,
      deliveryFee: data.deliveryFee ?? null,
      deliveryLatitude: null,
      deliveryLongitude: null,
      // Empty, not the order's earlier instruction. That note is already on
      // the ticket it was written for; loading it back in here sent it again
      // with whatever was added, and the kitchen read "no lettuce" under a
      // round of drinks. A note typed now is for what is being added now.
      notes: '',
      promotionCode: '',
      discount: data.discount ?? null,
      addingTo: { orderId: data.id, orderNumber: data.orderNumber, label },
    });
  },

  setOrderType(orderType) {
    // A zone or a hand-set fee only means anything on a delivery. Left set, a
    // takeaway would quote a delivery fee the customer is not paying.
    //
    // And a table belongs to a dine-in order only. A cashier who picks a table
    // and then switches to takeaway must not carry the table with them — left
    // set, it is sent with the order and the kitchen reads "Takeaway · Table 1".
    const dineIn = orderType === 'dine_in';
    set({
      orderType,
      deliveryZoneId: orderType === 'delivery' ? get().deliveryZoneId : null,
      deliveryFee: orderType === 'delivery' ? get().deliveryFee : null,
      tableId: dineIn ? get().tableId : null,
      tableNumber: dineIn ? get().tableNumber : '',
    });
  },

  /** Set (a number) or clear (null) the hand-typed delivery fee. */
  setDeliveryFee(deliveryFee) {
    set({ deliveryFee: deliveryFee == null || deliveryFee === '' ? null : Number(deliveryFee) });
  },

  /** Where the pin was dropped, or cleared when the address changes. */
  setDeliveryPin(latitude, longitude) {
    set({ deliveryLatitude: latitude ?? null, deliveryLongitude: longitude ?? null });
  },

  setDeliveryZone(deliveryZoneId) {
    set({ deliveryZoneId });
  },

  setField(field, value) {
    /**
     * Editing the name or number by hand un-picks whoever was chosen.
     *
     * The id means "this order is for that person". Once a cashier has changed
     * the details it may no longer be, and sending a stale id would attach the
     * order to the wrong customer — quietly, and showing up later as somebody
     * else's address sitting on their record.
     */
    const unpicks = field === 'customerName' || field === 'customerPhone';
    set(unpicks ? { [field]: value, customerId: null } : { [field]: value });
  },

  /**
   * A customer chosen from the suggestions: their details, and who they are.
   *
   * The address is only filled when they have one, so picking a takeaway
   * regular does not blank an address the cashier has already typed.
   */
  useKnownCustomer(customer) {
    set({
      customerId: customer.id,
      customerName: customer.name ?? '',
      customerPhone: customer.phone ?? '',
      ...(customer.address ? { deliveryAddress: customer.address } : {}),
    });
  },

  /** How many items, counting quantities — what the badge shows. */
  count() {
    return get().lines.reduce((n, l) => n + l.quantity, 0);
  },

  /**
   * The estimate shown on screen.
   *
   * Tax is not included. The server applies the rules for this branch — and
   * there are currently two active authorities charging different rates, so
   * guessing here would be wrong as often as right.
   */
  subtotal() {
    return round2(sumMoney(get().lines.map(lineTotal)));
  },

  /** The order body the backend expects. */
  toOrder() {
    const state = get();

    return {
      orderType: state.orderType,
      ...(state.orderType === 'delivery' && state.deliveryZoneId
        ? { deliveryZoneId: state.deliveryZoneId }
        : {}),
      // The hand-set fee, only on a delivery that has one; otherwise the server
      // works it out from the zone or the default.
      ...(state.orderType === 'delivery' && state.deliveryFee != null
        ? { deliveryFee: state.deliveryFee }
        : {}),
      items: state.lines.map((line) => ({
        productId: line.productId,
        ...(line.variantId ? { variantId: line.variantId } : {}),
        quantity: line.quantity,
        ...(line.modifiers?.length
          ? { modifiers: line.modifiers.map((m) => ({ optionId: m.optionId })) }
          : {}),
        ...(line.components?.length
          ? {
              components: line.components.map((c) => ({
                productId: c.productId,
                quantity: c.quantity,
                ...(c.modifiers?.length ? { modifiers: c.modifiers.map((m) => ({ optionId: m.optionId })) } : {}),
              })),
            }
          : {}),
        ...(line.notes ? { notes: line.notes } : {}),
      })),
      ...(state.customerId ? { customerId: state.customerId } : {}),
      ...(state.customerName ? { customerName: state.customerName } : {}),
      ...(state.customerPhone ? { customerPhone: state.customerPhone } : {}),
      ...(state.orderType === 'delivery' && state.deliveryAddress
        ? { deliveryAddress: state.deliveryAddress }
        : {}),
      /**
       * Sent only on a delivery that has a pin.
       *
       * Left out rather than sent as null, because the branch reads its
       * absence as "use whatever was pinned for this number last time" — and
       * an explicit null would look like somebody deliberately clearing it.
       */
      ...(state.orderType === 'delivery' && state.deliveryLatitude != null
        ? {
            deliveryLatitude: state.deliveryLatitude,
            deliveryLongitude: state.deliveryLongitude,
          }
        : {}),
      ...(state.tableNumber ? { tableNumber: state.tableNumber } : {}),
      // The Foodpanda app's order number, only on a Foodpanda order.
      ...(state.orderType === 'foodpanda' && state.externalOrderId?.trim()
        ? { externalOrderId: state.externalOrderId.trim() }
        : {}),
      // The one instruction for the whole order — "cut the burger in half". It
      // goes as specialInstructions, which is the field the kitchen ticket
      // prints under the order.
      ...(state.notes ? { specialInstructions: state.notes } : {}),
      ...(state.tableId ? { tableId: state.tableId } : {}),
      ...(state.promotionCode ? { promotionCode: state.promotionCode } : {}),
      ...(state.discount ? { discounts: [state.discount] } : {}),
    };
  },

  /** The id is the link; the number is what gets printed on the bill. */
  setTable(tableId, tableNumber) {
    set({ tableId: tableId ?? null, tableNumber: tableNumber ?? '' });
  },

  setPromotionCode(code) {
    set({
      promotionCode: String(code ?? '')
        .trim()
        .toUpperCase(),
    });
  },

  /** @param {null|{type:'percentage'|'fixed', value:number, reason?:string}} discount */
  setDiscount(discount) {
    set({ discount: discount && discount.value > 0 ? discount : null });
  },
}));

export { lineKey, lineTotal };
